import type { RestApiDayAvailabilityDto } from '@order-mage/integration-sdk';
import dayjs from 'dayjs';
import type { MessageKey, Translate } from '@/shared/i18n/translate';

export type FormattedVenueOpeningHour = {
    day: string;
    /** 0 = Sunday … 6 = Saturday */
    dayOfWeek: number;
    intervals: Array<{ start: string; end: string }>;
    /** ISO strings for exact open/closed checks — same length/order as `intervals`. */
    absoluteIntervals: Array<{ start: string; end: string }>;
};

/** HH:mm from ISO-8601, keeping the venue-local clock (no timezone conversion). */
const getIsoTime = (iso: string) => iso.slice(11, 16);

/** YYYY-MM-DD from ISO-8601. */
const getIsoDate = (iso: string) => iso.slice(0, 10);

const getMinutes = (hhmm: string) => {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
};

const getHhMm = (totalMinutes: number) => {
    if (totalMinutes === 1440) {
        return '00:00';
    }
    const normalized = totalMinutes > 1440 ? totalMinutes - 1440 : totalMinutes;
    const h = Math.floor(normalized / 60);
    const m = normalized % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/** First 7 unique weekdays in chronological order + optional next day for midnight wrap. */
const getWeekWindow = (openingHours: RestApiDayAvailabilityDto[]) => {
    const sorted = [...openingHours].sort((a, b) => a.date.localeCompare(b.date));
    const week: RestApiDayAvailabilityDto[] = [];
    const seenDows = new Set<number>();
    let lastIdx = -1;

    for (let i = 0; i < sorted.length; i++) {
        const dow = dayjs(getIsoDate(sorted[i].date)).day();
        if (seenDows.has(dow)) {
            continue;
        }
        seenDows.add(dow);
        week.push(sorted[i]);
        lastIdx = i;
        if (week.length === 7) {
            break;
        }
    }

    const nextDay = sorted[lastIdx + 1];
    const midnightOnly = (nextDay?.availableIntervals ?? []).filter(
        (interval) => getIsoTime(interval.start) === '00:00',
    );
    const lookahead =
        nextDay && midnightOnly.length > 0 ? { ...nextDay, availableIntervals: midnightOnly } : null;

    return { week, lookahead };
};

const getDayRow = (oh: RestApiDayAvailabilityDto, t: Translate): FormattedVenueOpeningHour => {
    const dayOfWeek = dayjs(getIsoDate(oh.date)).day();
    const parsed = (oh.availableIntervals ?? [])
        .map((interval) => ({
            start: getIsoTime(interval.start),
            end: getIsoTime(interval.end),
            absoluteStart: interval.start,
            absoluteEnd: interval.end,
        }))
        .sort((a, b) => a.start.localeCompare(b.start));

    return {
        day: t(`DAYS.${dayOfWeek}` as MessageKey),
        dayOfWeek,
        intervals: parsed.map(({ start, end }) => ({ start, end })),
        absoluteIntervals: parsed.map(({ absoluteStart, absoluteEnd }) => ({
            start: absoluteStart,
            end: absoluteEnd,
        })),
    };
};

/** Fold “ends at 00:00” into the next day’s “starts at 00:00” continuation. */
const mergeMidnightWraps = (days: FormattedVenueOpeningHour[]) => {
    for (let i = 0; i < days.length - 1; i++) {
        const today = days[i];
        const tomorrow = days[i + 1];

        for (let j = 0; j < today.intervals.length; j++) {
            if (today.intervals[j].end !== '00:00') {
                continue;
            }
            const wrapIdx = tomorrow.intervals.findIndex((interval) => interval.start === '00:00');
            if (wrapIdx < 0) {
                continue;
            }
            today.intervals[j].end = tomorrow.intervals[wrapIdx].end;
            today.absoluteIntervals[j].end = tomorrow.absoluteIntervals[wrapIdx].end;
            tomorrow.absoluteIntervals.splice(wrapIdx, 1);
            tomorrow.intervals.splice(wrapIdx, 1);
        }
    }
};

/** Merge overlapping / touching HH:mm intervals within one day (keeps absolute ISO in lockstep). */
const mergeOverlappingIntervals = (day: FormattedVenueOpeningHour) => {
    if (day.intervals.length <= 1) {
        return;
    }

    const asMinutes = day.intervals
        .map((interval, idx) => {
            const start = getMinutes(interval.start);
            let end = getMinutes(interval.end);
            if (interval.end === '00:00') {
                end = 1440;
            } else if (end <= start) {
                end += 1440;
            }
            const abs = day.absoluteIntervals[idx];
            return {
                start,
                end,
                absoluteStart: abs.start,
                absoluteEnd: abs.end,
            };
        })
        .sort((a, b) => a.start - b.start);

    const merged: Array<{
        start: number;
        end: number;
        absoluteStart: string;
        absoluteEnd: string;
    }> = [];
    let current = { ...asMinutes[0] };
    for (let i = 1; i < asMinutes.length; i++) {
        const next = asMinutes[i];
        if (next.start <= current.end) {
            if (next.end > current.end) {
                current.end = next.end;
                current.absoluteEnd = next.absoluteEnd;
            }
            continue;
        }
        merged.push(current);
        current = { ...next };
    }
    merged.push(current);

    day.intervals = merged.map(({ start, end }) => ({
        start: getHhMm(start),
        end: getHhMm(end),
    }));
    day.absoluteIntervals = merged.map(({ absoluteStart, absoluteEnd }) => ({
        start: absoluteStart,
        end: absoluteEnd,
    }));
};

const sortMondayFirst = (days: FormattedVenueOpeningHour[]) =>
    [...days].sort((a, b) => {
        const dayA = a.dayOfWeek === 0 ? 7 : a.dayOfWeek;
        const dayB = b.dayOfWeek === 0 ? 7 : b.dayOfWeek;
        return dayA - dayB;
    });

/**
 * Builds display opening hours: localized day names, HH:mm intervals,
 * midnight wrap-around fixed, overlapping slots merged. Monday → Sunday.
 */
export const getFormattedVenueOpeningHours = (
    openingHours: RestApiDayAvailabilityDto[],
    t: Translate,
): FormattedVenueOpeningHour[] => {
    if (!openingHours.length) {
        return [];
    }

    const { week, lookahead } = getWeekWindow(openingHours);
    const rows = [...week, ...(lookahead ? [lookahead] : [])].map((day) => getDayRow(day, t));

    mergeMidnightWraps(rows);

    const weekDays = rows.slice(0, 7);
    weekDays.forEach(mergeOverlappingIntervals);

    return sortMondayFirst(weekDays);
};
